import { useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { DraftResponse, DraftState } from '../types/protocol'

interface Props {
  draft: DraftState
  commissionerToken: string
  onDraft: (draft: DraftState) => void
}

export default function OrderRandomizer({ draft, commissionerToken, onDraft }: Props) {
  const [randomizing, setRandomizing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Disabled rather than hidden: a control that vanishes reads as a bug, and
  // the order is a thing the commissioner was allowed to shuffle five minutes
  // ago. No visible message — this renders inside TeamListEditor's header, and
  // that panel already says why; a second sentence is the same fact twice.
  const orderLocked = draft.lottery_revealed_at !== null
  const disabled =
    orderLocked || draft.status !== 'setup' || draft.teams.length < 2 || randomizing

  async function randomize() {
    setRandomizing(true)
    setError(null)
    try {
      const response = await api<DraftResponse>(`/drafts/${draft.join_code}/randomize_order`, {
        method: 'POST',
        commissionerToken,
      })
      onDraft(response.draft)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not randomize the order')
    } finally {
      setRandomizing(false)
    }
  }

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => void randomize()}
        disabled={disabled}
        title={orderLocked ? 'The lottery drew this order — it is final' : undefined}
        className="rounded border border-line-strong px-3 py-1.5 text-sm font-medium text-ink-hi hover:bg-surface-3 disabled:opacity-40"
      >
        {randomizing ? 'Shuffling…' : '🎲 Randomize order'}
      </button>
      {error && <span className="text-sm text-crit">{error}</span>}
    </div>
  )
}
